'use client';

import React, { useState, useMemo } from 'react';
import {
    Users,
    Search,
    Filter,
    Plus,
    GraduationCap,
    School,
    CheckCircle2,
    Eye,
    ChevronDown,
    Calendar,
    UserCircle,
    Phone,
    Download,
    ArrowLeft,
    Mail,
    MapPin,
    FileText,
    History,
    Save,
    X,
    BadgeCheck,
    Wallet,
    Info,
    Receipt,
    CreditCard,
    TrendingUp,
    ChevronRight,
    SearchIcon,
    AlertCircle
} from 'lucide-react';
import Link from 'next/link';

type ViewMode = 'list' | 'detail' | 'add' | 'edit';

export default function SiswaPage() {
    // --- State ---
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [selectedSiswa, setSelectedSiswa] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterClass, setFilterClass] = useState('Semua Kelas');

    // --- Mock Data ---
    const students = [
        {
            id: 'SIS-2425-001',
            name: 'Andi Pratama',
            class: 'TK A - Bintang',
            unit: 'PAUD Unit 1',
            parentName: 'Budi Santoso',
            parentPhone: '0812-3456-7890',
            status: 'Lunas',
            joiningDate: '15 Jul 2024',
            address: 'Jl. Melati No. 45, Pekanbaru',
            email: 'andi.p@gmail.com',
            birthDate: '12 Mei 2019',
            birthPlace: 'Pekanbaru',
            gender: 'Laki-laki',
            religion: 'Islam',
            bloodType: 'O'
        },
        {
            id: 'SIS-2425-002',
            name: 'Siti Kholifah',
            class: 'TK B - Bulan',
            unit: 'PAUD Unit 1',
            parentName: 'Ahmad Muzaki',
            parentPhone: '0812-9988-7766',
            status: 'Menunggak',
            joiningDate: '15 Jul 2024',
            address: 'Jl. Mawar Gg. Sejahtera, Pekanbaru',
            email: 'siti.k@gmail.com',
            birthDate: '05 Jan 2019',
            birthPlace: 'Pekanbaru',
            gender: 'Perempuan',
            religion: 'Islam',
            bloodType: 'A'
        },
        {
            id: 'SIS-2425-003',
            name: 'Rian Hidayat',
            class: 'Playgroup - Matahari',
            unit: 'PAUD Unit 2',
            parentName: 'Heri Gunawan',
            parentPhone: '0813-1122-3344',
            status: 'Lunas',
            joiningDate: '01 Aug 2024',
            address: 'Simpang Tiga, Siak',
            email: 'rian.h@gmail.com',
            birthDate: '20 Sep 2021',
            birthPlace: 'Siak',
            gender: 'Laki-laki',
            religion: 'Islam',
            bloodType: 'B'
        }
    ];

    // --- Memoized Values ---
    const classes = useMemo(() => {
        const unique = Array.from(new Set(students.map(s => s.class)));
        return ['Semua Kelas', ...unique];
    }, [students]);

    const filteredStudents = useMemo(() => {
        return students.filter(s => {
            const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.parentName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesClass = filterClass === 'Semua Kelas' || s.class === filterClass;
            return matchesSearch && matchesClass;
        });
    }, [searchTerm, filterClass, students]);

    // --- Action Handlers ---
    const handleViewDetail = (siswa: any) => {
        setSelectedSiswa(siswa);
        setViewMode('detail');
    };

    const handleAddClick = () => {
        setSelectedSiswa(null);
        setViewMode('add');
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedSiswa(null);
    };

    // --- Sub-components (Views) ---

    // 1. DETAIL VIEW
    const DetailView = () => {
        if (!selectedSiswa) return null;
        return (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
                {/* Header Detail */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToList}
                            className="p-3 bg-white border border-border-main rounded-2xl hover:bg-primary-soft hover:text-primary transition-all shadow-sm group"
                        >
                            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight text-foreground">{selectedSiswa.name}</h1>
                            <p className="text-secondary font-bold uppercase tracking-widest text-[10px] flex items-center gap-2">
                                <GraduationCap className="w-3.5 h-3.5 text-primary" /> Detail Profil Siswa • {selectedSiswa.id}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setViewMode('edit')} className="px-6 py-3 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Ubah Data
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Sidebar: Informasi Dasar (Sesuai Usecase) */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 text-center relative overflow-hidden group shadow-sm">
                            <div className="absolute top-0 left-0 w-full h-2 gradient-green"></div>
                            <div className="w-32 h-32 bg-primary-soft rounded-[2.5rem] mx-auto mb-6 flex items-center justify-center border-4 border-white shadow-xl relative transform group-hover:rotate-3 transition-transform">
                                <span className="text-4xl font-black text-primary">{selectedSiswa.name.charAt(0)}</span>
                            </div>
                            <h3 className="text-xl font-black text-foreground">{selectedSiswa.name}</h3>
                            <p className="text-xs font-bold text-muted uppercase tracking-widest mt-1">{selectedSiswa.class}</p>

                            <div className="mt-8 pt-8 border-t border-border-main/50 space-y-4 text-left">
                                <h4 className="text-[10px] font-black text-muted uppercase tracking-[0.2em] mb-4">Informasi Dasar</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted font-bold">ID SISWA</span>
                                        <span className="text-foreground font-black tracking-tight">{selectedSiswa.id}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted font-bold">UNIT</span>
                                        <span className="text-foreground font-black tracking-tight">{selectedSiswa.unit}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted font-bold">TEMPAT LAHIR</span>
                                        <span className="text-foreground font-black tracking-tight">{selectedSiswa.birthPlace}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted font-bold">TGL LAHIR</span>
                                        <span className="text-foreground font-black tracking-tight">{selectedSiswa.birthDate}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-muted font-bold">GENDER</span>
                                        <span className="text-foreground font-black tracking-tight">{selectedSiswa.gender}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Orang Tua Quick Info */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 space-y-4 shadow-sm">
                            <p className="text-[10px] font-black text-muted uppercase tracking-widest">Kontak Orang Tua</p>
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-primary shadow-sm hover:scale-110 transition-transform">
                                    <UserCircle className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-black text-foreground uppercase">{selectedSiswa.parentName}</p>
                                    <p className="text-[10px] text-success font-bold flex items-center gap-1">
                                        <Phone className="w-3 h-3" /> {selectedSiswa.parentPhone}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Konten Utama: Riwayat Pembayaran (Sesuai Usecase) */}
                    <div className="lg:col-span-8 space-y-8">
                        {/* Status Card */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-10 shadow-sm relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-20 -mt-20"></div>
                            <div className="relative space-y-3 text-center md:text-left">
                                <p className="text-[10px] font-black text-muted uppercase tracking-widest flex items-center gap-2 justify-center md:justify-start">
                                    <Wallet className="w-4 h-4 text-primary" /> Kolektibilitas SPP
                                </p>
                                <h3 className="text-3xl font-black text-foreground">Status: <span className={selectedSiswa.status === 'Lunas' ? 'text-success' : 'text-error'}>{selectedSiswa.status}</span></h3>
                                <p className="text-xs font-medium text-secondary italic">Tagihan SPP bulan berjalan {selectedSiswa.status === 'Lunas' ? 'sudah diselesaikan' : 'belum dibayar'}.</p>
                            </div>
                            <div className="relative z-10 flex flex-col gap-2">
                                <button className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">Bayar Sekarang</button>
                                <button className="px-8 py-3 bg-white border border-border-main rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-primary-soft transition-all">Lihat Invoice</button>
                            </div>
                        </div>

                        {/* Riwayat Pembayaran Section */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] shadow-sm overflow-hidden">
                            <div className="px-10 py-7 border-b border-border-main bg-background/20 flex items-center justify-between">
                                <div>
                                    <h4 className="text-[11px] font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-3">
                                        <History className="w-5 h-5 text-primary" /> Riwayat Pembayaran
                                    </h4>
                                    <p className="text-[9px] text-muted font-bold uppercase tracking-wider mt-1">Daftar transaksi SPP & Iuran lainnya</p>
                                </div>
                                <div className="flex gap-2">
                                    <button className="p-2 border border-border-main rounded-xl hover:bg-white transition-all"><Download className="w-4 h-4 text-muted" /></button>
                                </div>
                            </div>
                            <div className="p-10">
                                <div className="space-y-4">
                                    {[
                                        { period: 'Februari 2026', type: 'SPP Bulanan', amount: 'Rp 750.000', date: '05 Feb 2026', method: 'Transfer Bank', status: 'Success' },
                                        { period: 'Januari 2026', type: 'SPP Bulanan', amount: 'Rp 750.000', date: '04 Jan 2026', method: 'Manual', status: 'Success' },
                                        { period: 'Desember 2025', type: 'SPP Bulanan + Ekskul', amount: 'Rp 950.000', date: '10 Dec 2025', method: 'Transfer Bank', status: 'Success' },
                                    ].map((pay, i) => (
                                        <div key={i} className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-background/50 border border-border-main/50 rounded-3xl group hover:border-primary/20 transition-all gap-4">
                                            <div className="flex items-center gap-5">
                                                <div className="w-12 h-12 rounded-2xl bg-white border border-border-main/50 flex items-center justify-center shadow-sm">
                                                    <CreditCard className="w-6 h-6 text-muted group-hover:text-primary transition-colors" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-foreground uppercase tracking-tight">{pay.period}</p>
                                                    <div className="flex items-center gap-3 mt-0.5">
                                                        <span className="text-[9px] font-bold text-muted uppercase">{pay.type}</span>
                                                        <span className="w-1 h-1 rounded-full bg-border-main"></span>
                                                        <span className="text-[9px] font-bold text-secondary uppercase">{pay.method}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between md:text-right gap-6">
                                                <div className="text-left md:text-right">
                                                    <p className="text-sm font-black text-foreground tracking-tight">{pay.amount}</p>
                                                    <p className="text-[9px] text-muted font-bold uppercase">{pay.date}</p>
                                                </div>
                                                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center text-success">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="w-full mt-8 py-4 border border-dashed border-border-main rounded-2xl text-[10px] font-black text-muted uppercase hover:border-primary hover:text-primary transition-all">
                                    Tampilkan Riwayat Lebih Tua
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // 2. ADD/EDIT VIEW (Sesuai Usecase)
    const FormView = () => {
        const isEdit = viewMode === 'edit';
        return (
            <div className="space-y-8 animate-in slide-in-from-bottom duration-500 pb-20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToList}
                            className="p-3 bg-white border border-border-main rounded-2xl hover:bg-primary-soft hover:text-primary transition-all shadow-sm group"
                        >
                            <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight text-foreground">{isEdit ? 'Ubah Data Siswa' : 'Penambahan Siswa'}</h1>
                            <p className="text-secondary font-bold uppercase tracking-widest text-[10px] flex items-center gap-2">
                                <Plus className="w-3.5 h-3.5 text-primary" /> Manajemen Database Anak Didik
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
                    {/* Section: Data Siswa (Usecase) */}
                    <div className="space-y-6">
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-10 space-y-8 shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-2 h-full gradient-green opacity-50"></div>
                            <h3 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-3">
                                <GraduationCap className="w-5 h-5 text-primary" /> 01. Informasi Data Siswa
                            </h3>

                            <div className="space-y-6">
                                <div className="space-y-2 group">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1 group-focus-within:text-primary transition-colors">Nama Lengkap Anak</label>
                                    <input type="text" defaultValue={selectedSiswa?.name} placeholder="Nama lengkap sesuai akte" className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm focus:ring-4 focus:ring-primary/5 focus:border-primary outline-none transition-all" />
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Jenis Kelamin</label>
                                        <select className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none cursor-pointer">
                                            <option>Laki-laki</option>
                                            <option>Perempuan</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Agama</label>
                                        <select className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none cursor-pointer">
                                            <option>Islam</option>
                                            <option>Kristen</option>
                                            <option>Katolik</option>
                                            <option>Budha</option>
                                            <option>Hindu</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Tempat Lahir</label>
                                        <input type="text" defaultValue={selectedSiswa?.birthPlace} placeholder="Kota lahir" className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Tanggal Lahir</label>
                                        <input type="date" defaultValue={selectedSiswa?.birthDate} className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none" />
                                    </div>
                                </div>

                                <div className="p-6 bg-primary-soft/30 border border-primary/10 rounded-3xl space-y-4">
                                    <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em]">Penugasan Akademik</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[9px] font-black text-muted uppercase ml-1">Unit Sekolah</label>
                                            <select className="w-full bg-white border border-border-main/50 rounded-xl py-2.5 px-4 text-xs font-bold outline-none">
                                                <option>PAUD Unit 1</option>
                                                <option>PAUD Unit 2</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[9px] font-black text-muted uppercase ml-1">Pilih Kelas</label>
                                            <select className="w-full bg-white border border-border-main/50 rounded-xl py-2.5 px-4 text-xs font-bold outline-none">
                                                <option>TK A - Bintang</option>
                                                <option>TK B - Bulan</option>
                                                <option>Playgroup</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: Data Orang Tua (Usecase) */}
                    <div className="space-y-6">
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-10 space-y-8 shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-2 h-full bg-primary/20 opacity-50"></div>
                            <h3 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-3">
                                <UserCircle className="w-5 h-5 text-primary" /> 02. Informasi Data Orang Tua
                            </h3>

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Nama Ayah / Ibu / Wali</label>
                                    <input type="text" defaultValue={selectedSiswa?.parentName} placeholder="Nama lengkap wali" className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none" />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Nomor WhatsApp Aktif</label>
                                    <div className="relative">
                                        <div className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center gap-2 border-r border-border-main pr-3">
                                            <span className="text-xs font-black text-foreground">+62</span>
                                        </div>
                                        <input type="text" defaultValue={selectedSiswa?.parentPhone} placeholder="812-7766-..." className="w-full bg-background border border-border-main rounded-2xl py-4.5 pl-20 pr-6 text-sm font-bold shadow-sm outline-none" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Alamat Domisili Tetap</label>
                                    <textarea rows={4} defaultValue={selectedSiswa?.address} placeholder="Jl. Nama Jalan, No House, Kelurahan, Kecamatan..." className="w-full bg-background border border-border-main rounded-2xl py-4.5 px-6 text-sm font-bold shadow-sm outline-none resize-none"></textarea>
                                </div>

                                <div className="p-6 bg-background rounded-3xl border border-dashed border-border-main text-center space-y-3 cursor-pointer hover:border-primary/50 transition-colors group">
                                    <div className="w-10 h-10 rounded-xl bg-white border border-border-main flex items-center justify-center mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                        <Plus className="w-5 h-5 text-muted group-hover:text-primary transition-colors" />
                                    </div>
                                    <p className="text-[10px] font-black text-muted uppercase tracking-widest">Unggah KK / KTP Wali (Max 2MB)</p>
                                </div>
                            </div>
                        </div>

                        {/* CTA Fixed / Floating effect wrapper */}
                        <div className="pt-4 flex flex-col gap-4">
                            <button onClick={handleBackToList} className="w-full py-5 gradient-green text-white rounded-[2rem] text-xs font-black uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 hover:scale-[1.02] transition-all flex items-center justify-center gap-3">
                                <Save className="w-5 h-5" /> {isEdit ? 'Perbaharui Database' : 'Submit Data Siswa'}
                            </button>
                            <p className="text-[9px] text-center text-muted font-bold uppercase tracking-widest">Pastikan seluruh data sudah tervalidasi sebelum melakukan penyimpanan.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // 3. MAIN LIST VIEW
    const ListView = () => (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header section */}
            <section className="bg-white p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="relative z-10 space-y-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <GraduationCap className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Data <span className="text-primary">Siswa</span></h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-widest">
                                    Database Kesiswaan & Monitoring SPP
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button className="px-6 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                <Download className="w-4 h-4 text-muted" /> Export Data
                            </button>
                            <button
                                onClick={handleAddClick}
                                className="px-6 py-3.5 gradient-green text-white rounded-2xl text-[10px] font-bold shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex items-center gap-2 uppercase tracking-widest"
                            >
                                <Plus className="w-4 h-4" /> Tambah Siswa Baru
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Table Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden min-h-[500px] flex flex-col">
                {/* Search & Filter Bar */}
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari nama siswa, ID, orang tua..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-4 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none font-medium"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-background border border-border-main px-5 py-2.5 rounded-2xl">
                            <Filter className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent border-none text-[10px] font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterClass}
                                onChange={(e) => setFilterClass(e.target.value)}
                            >
                                {classes.map(cls => (
                                    <option key={cls} value={cls}>{cls}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="overflow-x-auto grow">
                    <table className="w-full text-left font-medium">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Data Siswa (ID & Nama)</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Penugasan Kelas</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Wali / Orang Tua</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status Pembayaran</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredStudents.length > 0 ? filteredStudents.map((siswa) => (
                                <tr key={siswa.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-linear-to-br from-primary/10 to-primary/20 flex items-center justify-center text-primary font-black text-sm uppercase shadow-inner">
                                                {siswa.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-black text-foreground group-hover:text-primary transition-colors uppercase tracking-tight">{siswa.name}</p>
                                                <p className="text-[10px] text-muted font-bold tabular-nums">NIS: {siswa.id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 whitespace-nowrap">
                                        <div className="space-y-1">
                                            <p className="text-xs font-black text-foreground">{siswa.class}</p>
                                            <div className="flex items-center gap-1.5">
                                                <School className="w-3 h-3 text-muted/50" />
                                                <p className="text-[10px] text-secondary font-bold uppercase tracking-wider">{siswa.unit}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <UserCircle className="w-3.5 h-3.5 text-muted" />
                                                <p className="text-xs font-black text-foreground uppercase">{siswa.parentName}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Phone className="w-3 h-3 text-success/60" />
                                                <p className="text-[10px] text-muted font-bold tracking-tight">{siswa.parentPhone}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-center whitespace-nowrap">
                                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${siswa.status === 'Lunas'
                                            ? 'bg-success-soft text-success border-success/10'
                                            : 'bg-error-soft text-error border-error/10 animate-pulse'
                                            }`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${siswa.status === 'Lunas' ? 'bg-success' : 'bg-error'}`}></div>
                                            {siswa.status}
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => handleViewDetail(siswa)}
                                            className="w-11 h-11 rounded-[14px] bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary hover:shadow-lg transition-all"
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center">
                                        <p className="text-sm text-secondary font-bold uppercase tracking-widest">Data siswa tidak ditemukan</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="p-8 border-t border-border-main bg-background/10 flex justify-between items-center">
                    <p className="text-[10px] font-black text-muted uppercase tracking-widest">
                        Total {filteredStudents.length} Database Siswa
                    </p>
                    <div className="flex gap-2">
                        <button className="px-5 py-2.5 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Previous</button>
                        <button className="px-5 py-2.5 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Next Page</button>
                    </div>
                </div>
            </section>
        </div>
    );

    return (
        <main className="p-4 md:p-10 max-w-7xl mx-auto w-full pb-24">
            {viewMode === 'list' && <ListView />}
            {viewMode === 'detail' && <DetailView />}
            {(viewMode === 'add' || viewMode === 'edit') && <FormView />}
        </main>
    );
}
